import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Rectangle,
  Circle,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { getMapProvider } from "../../config/mapProviders";
import { Maximize2, Minimize2 } from "lucide-react";
import BasemapSwitcher from "./BasemapSwitcher";
import {
  Bounds,
  ExtentPickerProps,
  FIT_DEBOUNCE_MS,
  INTRO_FLIGHT_MS,
  INTRO_HOLD_MS,
  RADIUS_MAX_M,
  RADIUS_MIN_M,
  RADIUS_STEP_M,
  extentBounds,
} from "./types";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

/** Matches the other renderers: the extent box must never be cropped. */
const FIT_PADDING_PX = 44;

// Leaflet's default icon URLs don't survive bundling — same fix as MapPicker.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Basemap comes from the pluggable registry — see src/config/mapProviders.ts */
const PROVIDER = getMapProvider();

/**
 * Leaflet caches its container size at init and does not observe it. Without this
 * the map "sticks": tiles stay at the old dimensions and the centre drifts off
 * after any resize, panel reflow, or orientation change.
 */
const KeepSized: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => map.invalidateSize({ animate: false }));
    };
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener("orientationchange", sync);
    sync();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("orientationchange", sync);
    };
  }, [map]);
  return null;
};

/**
 * Keeps the whole extent in frame. Without this, widening the radius grows the
 * rectangle past the viewport and the user loses sight of what they selected.
 */
const FitExtent: React.FC<{ bounds: Bounds; ready: boolean; suppress: boolean }> = ({
  bounds,
  ready,
  suppress,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!ready || suppress) return;
    // Raster tiles bill per request, so a refit per slider tick is expensive.
    const id = window.setTimeout(
      () => map.fitBounds(bounds, { padding: [FIT_PADDING_PX, FIT_PADDING_PX], animate: true, maxZoom: fittedZoom(map, bounds) }),
      FIT_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(id);
  }, [ready, bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

/** Flies from continent level down to the selected extent once, on mount. */
const IntroFlight: React.FC<{
  bounds: Bounds;
  center: [number, number];
  onDone: () => void;
}> = ({ bounds, center, onDone }) => {
  const map = useMap();
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      map.fitBounds(bounds, { padding: [FIT_PADDING_PX, FIT_PADDING_PX], animate: false, maxZoom: fittedZoom(map, bounds) });
      onDone();
      return;
    }
    map.setView(center, 4, { animate: false });
    const id = window.setTimeout(() => {
      map.flyToBounds(bounds, { padding: [FIT_PADDING_PX, FIT_PADDING_PX], duration: INTRO_FLIGHT_MS / 1000, maxZoom: fittedZoom(map, bounds) });
      map.once("moveend", onDone);
    }, INTRO_HOLD_MS);
    return () => window.clearTimeout(id);
    // Intro runs once per mount; later extent changes belong to FitExtent.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};


/** One step out from the fitted zoom — see the note in GoogleMapsExtentPicker. */
const ZOOM_BIAS = -1;

/* Leaflet's fitBounds has no bias option; getBoundsZoom reports the level it
   would choose, so maxZoom holds the camera one step short of it.
   Typed structurally: @types/leaflet resolves as a UMD namespace here, so a
   named type import for LatLngBoundsExpression fails with TS2709. */
function fittedZoom(map: { getBoundsZoom: (b: Bounds) => number }, bounds: Bounds): number {
  return map.getBoundsZoom(bounds) + ZOOM_BIAS;
}

const RasterMapsExtentPicker: React.FC<ExtentPickerProps> = ({
  center,
  address,
  radiusM,
  onRadiusChange,
  compact = false,
  coverage,
  disabled = false,
  readOnly = false,
  expanded = false,
  onToggleExpand,
  onCenterChange,
  caption,
}) => {
  const [introDone, setIntroDone] = useState(readOnly);
  const [variantId, setVariantId] = useState(PROVIDER.variants[0].id);
  const variant = PROVIDER.variants.find((v) => v.id === variantId) ?? PROVIDER.variants[0];
  const pos: [number, number] = [center.lat, center.lon];
  const bounds = extentBounds(center.lat, center.lon, radiusM);
  const side = Math.round(radiusM * 2);
  const canDragPin = !!onCenterChange && !readOnly && !disabled;
  /** Last centre this component itself produced, by drag. */
  const selfMovedRef = useRef<{ lat: number; lon: number } | null>(null);
  const self = selfMovedRef.current;
  const fromDrag =
    !!self && Math.abs(self.lat - center.lat) < 1e-9 && Math.abs(self.lon - center.lon) < 1e-9;

  // Consume the flag after the render that used it — clearing it during render
  // would be a side effect. Left set, it swallowed every later radius change.
  useEffect(() => { if (fromDrag) selfMovedRef.current = null; }, [fromDrag]);

  return (
    <div className={expanded
      ? 'flex h-full min-h-0 flex-col gap-2'
      : 'flex flex-col gap-2'}>
      {/* Caption and the expand affordance share one row: the caption's left
          edge lines up with the map, the button's right edge with the info
          column, so the panel reads as one block instead of three stacked
          rows. Both sit at text-ui, matching the chat callout above it. */}
      {(caption || onToggleExpand) && (
        <div className="flex items-center gap-2">
          {caption && <span className="min-w-0 flex-1 text-ui text-ink-200">{caption}</span>}
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              /* Visible text is one word so the chip stays small; the accessible
                 name keeps the noun, which "Expand" alone would not carry. */
              aria-label={expanded ? "Shrink map" : "Expand map"}
              className={`ml-auto flex w-fit flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-ui font-medium transition-colors focus:outline-none focus-visible:ring-2 ${
                expanded
                  ? "bg-crane-500 text-ink-950 hover:bg-crane-600 focus-visible:ring-crane-400"
                  : "border border-ink-600 bg-ink-850 text-ink-200 hover:border-crane-500/60 hover:bg-ink-800 hover:text-ink-100 focus-visible:ring-crane-500"
              }`}
            >
              {expanded
                ? <><Minimize2 className="h-3.5 w-3.5" /> Shrink</>
                : <><Maximize2 className="h-3.5 w-3.5 text-crane-500" /> Expand</>}
            </button>
          )}
        </div>
      )}

      <div className={expanded
        ? 'flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:gap-5'
        : 'flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-5'}>
      {/* Map gets the space; the extent control is a narrow rail beside it */}
          {/* Collapsed, the map is a flex item beside the info column. A fixed
              height left it shorter than that column whenever the address
              wrapped to two lines or coverage appeared, stranding dead space
              next to it — so it carries a floor and stretches to whatever the
              row's height turns out to be. */}
      <div
        className={expanded
          ? 'relative min-h-0 w-full min-w-0 flex-1 overflow-hidden rounded-xl border border-crane-500/30'
          : `relative w-full min-w-0 overflow-hidden rounded-lg border border-crane-500/30 sm:flex-[3] ${
              compact ? 'min-h-[11rem]' : 'min-h-[15rem]'
            }`}
      >
        <MapContainer
          center={pos}
          zoom={4}
          style={{ position: 'absolute', inset: 0 }}
          scrollWheelZoom={false}
          dragging={!disabled && !readOnly}
        >
          <TileLayer
            key={variant.id}
            url={variant.url}
            attribution={PROVIDER.attribution}
            subdomains={PROVIDER.subdomains ?? "abc"}
            maxZoom={PROVIDER.maxZoom ?? 19}
          />
          <Marker
            position={pos}
            draggable={canDragPin}
            /* Typed structurally, not as Leaflet's DragEndEvent: this project
               resolves @types/leaflet as a UMD namespace, so named type imports
               from "leaflet" fail with TS2709. LeafletEvent.target is `any`
               upstream, so naming it buys nothing. */
            eventHandlers={{
              dragend: (e: { target: { getLatLng(): { lat: number; lng: number } } }) => {
                if (!canDragPin) return;
                const next = e.target.getLatLng();
                const moved = { lat: next.lat, lon: next.lng };
                // Remember it so the prop echoing back does not refit the camera.
                selfMovedRef.current = moved;
                onCenterChange?.(moved);
              },
            }}
          />
          {/* The square is what actually gets reconstructed. */}
          <Rectangle bounds={bounds} pathOptions={{ color: '#4FCBEE', weight: 3, fillOpacity: 0.16 }} />
          {/* The circle is the radius handle — inscribed, reference only. */}
          <Circle
            center={pos}
            radius={radiusM}
            pathOptions={{ color: '#0C5484', weight: 1, dashArray: '4 4', fill: false }}
          />
          {!readOnly && (
            <IntroFlight bounds={bounds} center={pos} onDone={() => setIntroDone(true)} />
          )}
          <FitExtent bounds={bounds} ready={introDone} suppress={fromDrag} />
          <KeepSized />
        </MapContainer>
        {!readOnly && (
          <BasemapSwitcher
            variants={PROVIDER.variants}
            activeId={variant.id}
            onChange={setVariantId}
            disabled={disabled}
          />
        )}
      </div>

      {/* Controls sit beside the map, not under it */}
      <div className={expanded
        ? 'flex flex-shrink-0 flex-col gap-4 lg:w-80 lg:overflow-y-auto'
        : 'flex min-w-0 flex-col justify-center gap-3 sm:flex-1 sm:pl-1'}>


        {/* Same scale as the chat: meta labels, body values. */}
        <dl className="space-y-4">
          <div>
            <dt className="text-meta text-ink-400">Place</dt>
            <dd className="line-clamp-2 text-ui text-ink-100" title={address}>{address}</dd>
          </div>

          <div>
            <dt className="text-meta text-ink-400">Coordinates</dt>
            <dd className="font-mono text-ui tabular-nums text-ink-200">
              {center.lat.toFixed(5)}, {center.lon.toFixed(5)}
            </dd>
            {onCenterChange && !readOnly && (
              <dd className="mt-0.5 text-meta text-ink-500">Drag the pin to reposition</dd>
            )}
          </div>

          <div>
            <dt className="text-meta text-ink-400">Selected area</dt>
            <dd className="text-ui font-medium text-crane-500">{side} &times; {side} m</dd>
            {!readOnly && (
              <input
                type="range"
                min={RADIUS_MIN_M}
                max={RADIUS_MAX_M}
                step={RADIUS_STEP_M}
                value={radiusM}
                disabled={disabled}
                onChange={(e) => onRadiusChange(Number(e.target.value))}
                aria-label="Reconstruction extent in metres"
                className="mt-2 w-full max-w-[14rem] accent-crane-500 disabled:opacity-50"
              />
            )}
          </div>

          {coverage && (
            <div>
              <dt className="text-meta text-ink-400">Imagery</dt>
              <dd className="text-ui text-ink-200">{coverage.year}</dd>
            </div>
          )}
        </dl>
        </div>
      </div>
    </div>
  );
};

export default RasterMapsExtentPicker;
